import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  MessageCircle, Send, RefreshCw, Users, Wifi, WifiOff,
  ChevronLeft, Search, Bot, Clock, FileText, 
  Play, Download, X, Edit2, Check, Plus, Phone, FolderDown, UserCircle, Shield, Paperclip, Mic, Square, Loader2, Image as ImageIcon, Reply, CornerUpLeft
} from 'lucide-react';
import { toast } from 'sonner';
import useWhatsAppMessages from '../hooks/useWhatsAppMessages';

const WA_API = '/wa-api';
const WHATSAPP_NOTIFICATION_SOUND = 'https://cdn.pixabay.com/download/audio/2021/08/04/audio_bb630cc098.mp3?filename=message-13716.mp3';

export default function WhatsAppConversations() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
const [replyingTo, setReplyingTo] = useState(null); // Mensaje al que se responde
  const [searchQuery, setSearchQuery] = useState('');
  const [botStatus, setBotStatus] = useState({ status: 'checking' });
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatName, setNewChatName] = useState('');
  const [sendingNew, setSendingNew] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const shouldScrollBottomRef = useRef(true);
  const suppressOlderLoadUntilRef = useRef(0);
  const inputRef = useRef(null);
  const selectedConvRef = useRef(null);
  const audioRef = useRef(null);
  const mentionCursorRef = useRef(0);
  const loadingMentionParticipantsRef = useRef(false);

  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);

  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) { audioRef.current = new Audio(WHATSAPP_NOTIFICATION_SOUND); audioRef.current.volume = 0.5; }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) {}
  }, []);

  const getMediaPreview = (type) => {
    const previews = { image: '📷 Imagen', video: '🎥 Video', audio: '🎵 Audio', document: '📄 Documento', sticker: '🏷️ Sticker' };
    return previews[type] || '[Mensaje]';
  };

  const handleNewMessage = useCallback((data) => {
    if (data?.message_type === 'reaction' && data?.extra_meta?.reaction_target) {
      handleMessageReaction({
        message_id: data.extra_meta.reaction_target,
        group_id: data.group_id,
        reactions: [{ emoji: data.extra_meta.reaction_emoji, actor_name: data.from_name, is_from_me: data.is_from_me }],
        reaction_count: 1
      });
      return;
    }
    setConversations(prev => {
      const idx = prev.findIndex(c => c.group_id === data.group_id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], last_message: data.content || getMediaPreview(data.message_type), last_message_at: data.timestamp, last_message_from: data.from_name, unread_count: selectedConvRef.current?.group_id === data.group_id ? 0 : (updated[idx].unread_count || 0) + 1 };
        const [conv] = updated.splice(idx, 1);
        return [conv, ...updated];
      }
      loadConversations();
      return prev;
    });
    if (selectedConvRef.current?.group_id === data.group_id) {
      shouldScrollBottomRef.current = true;
      setMessages(prev => prev.some(msg => msg.id === data.id) ? prev : [...prev, data]);
      setTimeout(scrollToBottom, 100);
    } else if (!data.is_from_me) {
      playNotificationSound();
      toast.message('📱 ' + data.from_name, { description: data.content?.substring(0, 50) || getMediaPreview(data.message_type), duration: 5000 });
    }
  }, [playNotificationSound]);

  const handleMessageReaction = useCallback((data) => {
    if (!data?.message_id) return;
    setMessages(prev => prev.map(msg => {
      if (msg.id !== data.message_id) return msg;
      return {
        ...msg,
        reactions: data.reactions || [],
        reaction_count: data.reaction_count || (data.reactions || []).length
      };
    }));
  }, []);

  const handleBotStatus = useCallback((data) => setBotStatus(data), []);
  const { isConnected } = useWhatsAppMessages(handleNewMessage, handleBotStatus, handleMessageReaction);

  useEffect(() => { loadConversations(); checkBotStatus(); }, []);
  useEffect(() => { if (selectedConversation) { loadMessages(selectedConversation.group_id); markAsRead(selectedConversation.group_id); setShowMobileChat(true); } }, [selectedConversation]);
  useEffect(() => { if (selectedConversation) loadConversationProfile(selectedConversation.group_id); }, [selectedConversation?.group_id]);
  useEffect(() => {
    if (shouldScrollBottomRef.current) {
      scrollToBottom();
      shouldScrollBottomRef.current = false;
    }
  }, [messages]);

  const scrollToBottom = () => {
    const scroller = messagesScrollRef.current;
    if (scroller) {
      scroller.scrollTop = scroller.scrollHeight;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };
  const checkBotStatus = async () => { try { const res = await axios.get(WA_API + '/api/bot-status'); setBotStatus(res.data); } catch (e) { setBotStatus({ status: 'unreachable' }); } };
  const loadConversations = async () => { try { const res = await axios.get(WA_API + '/api/conversations'); setConversations(res.data.conversations || []); } catch (error) { toast.error('Error cargando conversaciones'); } finally { setLoading(false); } };
  const loadMessages = async (groupId) => {
    shouldScrollBottomRef.current = true;
    suppressOlderLoadUntilRef.current = Date.now() + 5000;
    setLoadingMessages(true);
    setHasMoreMessages(false);
    setNextBefore(null);
    try {
      const res = await axios.get(WA_API + '/api/conversations/' + encodeURIComponent(groupId) + '/messages', { params: { limit: 100 } });
      setMessages(res.data.messages || []);
      setHasMoreMessages(res.data.has_more === true);
      setNextBefore(res.data.next_before || null);
      setTimeout(scrollToBottom, 350);
      setTimeout(scrollToBottom, 1400);
    } catch (error) {
      console.error(error);
      toast.error('Error cargando mensajes');
    } finally {
      setLoadingMessages(false);
    }
  };
  const loadOlderMessages = async () => {
    if (!selectedConversation || !hasMoreMessages || loadingOlderMessages || !nextBefore) return;
    const scroller = messagesScrollRef.current;
    const previousHeight = scroller?.scrollHeight || 0;
    setLoadingOlderMessages(true);
    try {
      const res = await axios.get(WA_API + '/api/conversations/' + encodeURIComponent(selectedConversation.group_id) + '/messages', { params: { limit: 100, before: nextBefore } });
      const older = res.data.messages || [];
      shouldScrollBottomRef.current = false;
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        const mergedOlder = older.filter(m => !existing.has(m.id));
        return [...mergedOlder, ...prev];
      });
      setHasMoreMessages(res.data.has_more === true);
      setNextBefore(res.data.next_before || null);
      requestAnimationFrame(() => {
        if (scroller) scroller.scrollTop = (scroller.scrollHeight - previousHeight) + 24;
      });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar historial anterior');
    } finally {
      setLoadingOlderMessages(false);
    }
  };
  const handleMessagesScroll = () => {
    const scroller = messagesScrollRef.current;
    if (!scroller || loadingMessages || loadingOlderMessages) return;
    if (shouldScrollBottomRef.current) return;
    if (Date.now() < suppressOlderLoadUntilRef.current) return;
    if (scroller.scrollTop < 120 && hasMoreMessages) loadOlderMessages();
  };
  const markAsRead = async (groupId) => { try { await axios.post(WA_API + '/api/conversations/' + encodeURIComponent(groupId) + '/read'); setConversations(prev => prev.map(c => c.group_id === groupId ? { ...c, unread_count: 0 } : c)); } catch (e) {} };
  const loadConversationProfile = async (groupId) => {
    try {
      const res = await axios.get(WA_API + '/api/conversations/' + encodeURIComponent(groupId) + '/profile');
      const profile = res.data?.profile;
      if (!profile) return;
      setConversations(prev => prev.map(c => c.group_id === groupId ? { ...c, ...profile } : c));
      setSelectedConversation(prev => prev?.group_id === groupId ? { ...prev, ...profile } : prev);
    } catch (error) {
      console.warn('No se pudo sincronizar perfil WA', error.message);
    }
  };

  const isPrivateChat = (groupId) => groupId?.endsWith('@s.whatsapp.net');

  const handleImportGroups = async () => {
    try {
      toast.info('Importando grupos...');
      const res = await axios.post(WA_API + '/api/import-groups');
      if (res.data.success) {
        toast.success(`Importados: ${res.data.imported} nuevos, ${res.data.updated} actualizados`);
        loadConversations();
      }
    } catch (e) {
      toast.error('Error importando grupos');
    }
  };

  const handleShowParticipants = async () => {
    if (!selectedConversation || isPrivateChat(selectedConversation.group_id)) return;
    setLoadingParticipants(true);
    setShowParticipants(true);
    try {
      const res = await axios.get(WA_API + '/api/group/' + encodeURIComponent(selectedConversation.group_id) + '/participants');
      if (res.data.success) {
        setParticipants(res.data);
      } else {
        toast.error('Error cargando participantes');
        setShowParticipants(false);
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
      setShowParticipants(false);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const loadParticipantsForMentions = useCallback(async () => {
    if (!selectedConversation || isPrivateChat(selectedConversation.group_id)) return;
    if (participants?.group_id === selectedConversation.group_id) return;
    if (loadingMentionParticipantsRef.current) return;
    loadingMentionParticipantsRef.current = true;
    try {
      const res = await axios.get(WA_API + '/api/group/' + encodeURIComponent(selectedConversation.group_id) + '/participants');
      if (res.data.success) setParticipants(res.data);
    } catch (e) {
      console.warn('No se pudieron precargar participantes para menciones', e.message);
    } finally {
      loadingMentionParticipantsRef.current = false;
    }
  }, [selectedConversation, participants]);

  const handleSaveName = async () => {
    if (!newName.trim() || !selectedConversation) return;
    try {
      await axios.put(WA_API + '/api/conversations/' + encodeURIComponent(selectedConversation.group_id) + '/name', { name: newName.trim() });
      setConversations(prev => prev.map(c => c.group_id === selectedConversation.group_id ? { ...c, group_name: newName.trim() } : c));
      setSelectedConversation(prev => ({ ...prev, group_name: newName.trim() }));
      setEditingName(false);
      toast.success('Nombre actualizado');
    } catch (e) { toast.error('Error actualizando nombre'); }
  };

  const handleSendNewChat = async () => {
    if (!newChatPhone.trim() || !newMessage.trim() || sendingNew) return;
    if (botStatus.status !== 'connected') { toast.error('El bot no está conectado'); return; }
    
    setSendingNew(true);
    try {
      const res = await axios.post(WA_API + '/api/send-new', {
        phone: newChatPhone.trim(),
        content: newMessage.trim(),
        name: newChatName.trim() || null
      });
      
      if (res.data.success) {
        toast.success('Mensaje enviado');
        setShowNewChat(false);
        setNewChatPhone('');
        setNewChatName('');
        setNewMessage('');
        loadConversations();
        // Seleccionar la nueva conversación
        setSelectedConversation({
          group_id: res.data.group_id,
          group_name: newChatName.trim() || '+' + newChatPhone.replace(/\D/g, '')
        });
      }
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingNew(false);
    }
  };


  // === FUNCIONES PARA ADJUNTAR ARCHIVOS ===
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tamaño máximo 16MB
      if (file.size > 16 * 1024 * 1024) {
        toast.error('El archivo es muy grande (máx 16MB)');
        return;
      }
      setSelectedFile(file);
      toast.info('Archivo seleccionado: ' + file.name);
    }
  };

  const handleSendFile = async () => {
    if (!selectedFile || !selectedConversation || sending) return;
    if (botStatus.status !== 'connected') { toast.error('El bot no está conectado'); return; }
    
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        await axios.post(WA_API + '/api/send-media', {
          group_id: selectedConversation.group_id,
          media_base64: base64,
          mimetype: selectedFile.type,
          filename: selectedFile.name,
          caption: newMessage.trim() || null
        });
        
        toast.success('Archivo enviado');
        setSelectedFile(null);
        setNewMessage('');
        setSending(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error('Error enviando archivo');
      setSending(false);
    }
  };

  // === FUNCIONES PARA GRABAR AUDIO ===
  // Función para dibujar la visualización del audio
  const drawAudioVisualization = () => {
    if (!canvasRef.current || !analyser) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      if (!isRecording) return;
      
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);
      
      ctx.fillStyle = 'rgb(254, 226, 226)'; // bg-red-100
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgb(239, 68, 68)'; // text-red-500
      ctx.beginPath();
      
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * canvas.height / 2;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }
      
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };
    
    draw();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Configurar visualizador de audio
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyserNode = audioCtx.createAnalyser();
      analyserNode.fftSize = 256;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyserNode);
      
      setAudioContext(audioCtx);
      setAnalyser(analyserNode);
      
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        // Limpiar visualización
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        stream.getTracks().forEach(t => t.stop());
        if (audioCtx) audioCtx.close();
        setAudioContext(null);
        setAnalyser(null);
        setRecordingTime(0);
        
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await sendAudioMessage(blob);
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Timer para mostrar duración
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.info('Grabando audio...');
      
      // Iniciar visualización después de un breve delay
      setTimeout(() => drawAudioVisualization(), 100);
      
    } catch (error) {
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
  };
  const sendAudioMessage = async (audioBlob) => {
    if (!selectedConversation || sending) return;
    if (botStatus.status !== 'connected') { toast.error('El bot no está conectado'); return; }
    
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        await axios.post(WA_API + '/api/send-media', {
          group_id: selectedConversation.group_id,
          media_base64: base64,
          mimetype: 'audio/webm',
          filename: 'audio_' + Date.now() + '.webm'
        });
        
        toast.success('Audio enviado');
        setSending(false);
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      toast.error('Error enviando audio');
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;
    if (botStatus.status !== 'connected') { toast.error('El bot no está conectado'); return; }
    setSending(true);
    try {
      const mentions = extractMentionJids(newMessage.trim());
      const payload = { 
        group_id: selectedConversation.group_id, 
        content: newMessage.trim()
      };
      if (mentions.length) payload.mentions = mentions;
      if (replyingTo) {
        payload.quoted_message_id = replyingTo.id;
        payload.quoted_content = replyingTo.content || '[Media]';
      }
      await axios.post(WA_API + '/api/send', payload);
      setNewMessage('');
      setMentionMenuOpen(false);
      setReplyingTo(null);
      inputRef.current?.focus();
    } catch (error) { toast.error('Error enviando mensaje'); } finally { setSending(false); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return date.toLocaleDateString('es-AR', { weekday: 'short' });
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateSeparator = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(date, today)) return 'Hoy';
    if (sameDay(date, yesterday)) return 'Ayer';
    return date.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  };

  const sameMessageDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a);
    const db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
  };

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return c.group_name?.toLowerCase().includes(query) || c.last_message?.toLowerCase().includes(query);
  });

  const participantPalette = [
    '#06cf9c', '#53bdeb', '#ffbc38', '#ff7b7b', '#b692f6', '#fc9775',
    '#2f80ed', '#00a884', '#ea80fc', '#f15c6d', '#7ed957', '#f2994a'
  ];
  const hashParticipant = (value = '') => {
    let hash = 0;
    const text = String(value || 'unknown');
    for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
    return Math.abs(hash);
  };
  const getParticipantColor = (msgOrName) => {
    const key = typeof msgOrName === 'string'
      ? msgOrName
      : (msgOrName?.from || msgOrName?.from_name || msgOrName?.id || 'unknown');
    return participantPalette[hashParticipant(key) % participantPalette.length];
  };

  const cleanMentionLabel = (value = '') => {
    const text = String(value || '').trim();
    if (!text || text === 'WINMANAGER' || text.length < 2) return null;
    if (/^[.\-_~]+$/.test(text)) return null;
    return text.replace(/^@+/, '');
  };
  const normalizeMentionKey = (value = '') => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_.ñ-]/g, '');
  const mentionTargets = useMemo(() => {
    const byJid = new Map();
    const addTarget = ({ label, phone, jid, source }) => {
      const cleanPhone = String(phone || '').replace(/\D/g, '');
      const targetJid = jid || (cleanPhone ? `${cleanPhone}@s.whatsapp.net` : null);
      if (!targetJid || targetJid.includes('@g.us')) return;
      const cleanLabel = cleanMentionLabel(label) || (cleanPhone ? `+${cleanPhone}` : null);
      if (!cleanLabel) return;
      const existing = byJid.get(targetJid);
      if (existing && existing.label && !/^\+?\d/.test(existing.label)) return;
      byJid.set(targetJid, { label: cleanLabel, phone: cleanPhone, jid: targetJid, source });
    };
    messages.forEach(msg => {
      if (msg.is_from_me) return;
      addTarget({ label: msg.from_name || msg.from, phone: msg.from, source: 'historial' });
    });
    if (participants && selectedConversation && participants.group_id === selectedConversation.group_id) {
      participants.participants?.forEach(p => addTarget({ label: p.name || p.phone, phone: p.phone, jid: p.id, source: 'grupo' }));
    }
    return Array.from(byJid.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [messages, participants, selectedConversation?.group_id]);
  const mentionSuggestions = useMemo(() => {
    if (!mentionMenuOpen) return [];
    const q = normalizeMentionKey(mentionQuery);
    return mentionTargets
      .filter(t => !q || normalizeMentionKey(t.label).includes(q) || t.phone.includes(q))
      .slice(0, 6);
  }, [mentionMenuOpen, mentionQuery, mentionTargets]);
  const extractMentionJids = useCallback((text = '') => {
    const tokens = Array.from(String(text).matchAll(/@([\p{L}\p{N}_.ÁÉÍÓÚÜÑáéíóúüñ-]+)/gu)).map(m => m[1]);
    const seen = new Set();
    const jids = [];
    tokens.forEach(token => {
      const cleanToken = normalizeMentionKey(token);
      const digits = token.replace(/\D/g, '');
      const target = mentionTargets.find(t =>
        normalizeMentionKey(t.label) === cleanToken ||
        (digits && t.phone.endsWith(digits.slice(-10)))
      );
      if (target && !seen.has(target.jid)) {
        seen.add(target.jid);
        jids.push(target.jid);
      }
    });
    return jids;
  }, [mentionTargets]);
  const handleMessageInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart || value.length;
    setNewMessage(value);
    mentionCursorRef.current = cursor;
    const beforeCursor = value.slice(0, cursor);
    const match = beforeCursor.match(/(?:^|\s)@([\p{L}\p{N}_.ÁÉÍÓÚÜÑáéíóúüñ-]{0,32})$/u);
    if (match && selectedConversation && !isPrivateChat(selectedConversation.group_id)) {
      setMentionQuery(match[1] || '');
      setMentionMenuOpen(true);
      loadParticipantsForMentions();
    } else {
      setMentionMenuOpen(false);
      setMentionQuery('');
    }
  }, [selectedConversation, loadParticipantsForMentions]);
  const insertMention = useCallback((target) => {
    const cursor = mentionCursorRef.current || newMessage.length;
    const before = newMessage.slice(0, cursor);
    const after = newMessage.slice(cursor);
    const replacedBefore = before.replace(/(^|\s)@([\p{L}\p{N}_.ÁÉÍÓÚÜÑáéíóúüñ-]{0,32})$/u, `$1@${target.label} `);
    const next = replacedBefore + after;
    const nextCursor = replacedBefore.length;
    setNewMessage(next);
    setMentionMenuOpen(false);
    setMentionQuery('');
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }, [newMessage]);
  const renderTextWithMentions = (text = '') => {
    const raw = String(text || '');
    const parts = raw.split(/(@[\p{L}\p{N}_.ÁÉÍÓÚÜÑáéíóúüñ-]+)/gu);
    return parts.map((part, idx) => {
      if (part.startsWith('@') && part.length > 1) {
        return (
          <span key={`${part}-${idx}`} className="font-semibold" style={{ color: getParticipantColor(part.slice(1)) }}>
            {part}
          </span>
        );
      }
      return <React.Fragment key={`${idx}-${part.slice(0, 6)}`}>{part}</React.Fragment>;
    });
  };

  const renderMessageContent = (msg) => {
    const { message_type, content, media_url, filename, mimetype } = msg;
    if (message_type === 'image') {
      if (!media_url) {
        return (
          <div className="flex items-center gap-2 text-gray-500 bg-gray-100 dark:bg-gray-600 rounded-lg px-4 py-3">
            <ImageIcon className="w-6 h-6 text-gray-400" />
            <span>📷 Imagen no disponible</span>
          </div>
        );
      }
      return (
        <div className="space-y-2">
          <button
            type="button"
            data-testid={`wa-message-image-preview-${msg.id}`}
            onClick={() => setImagePreview({ src: media_url, msg })}
            className="block w-[240px] max-w-[68vw] overflow-hidden rounded-lg bg-black/5 text-left transition-opacity hover:opacity-90 sm:w-[270px] md:w-[300px]"
          >
            <img src={media_url} alt="Comprobante o imagen de WhatsApp" className="block w-full h-auto object-contain" style={{ maxHeight: '420px' }} loading="lazy" />
          </button>
          {content && <p className="text-gray-900 dark:text-white">{content}</p>}
        </div>
      );
    }
    if (message_type === 'audio') {
      if (!media_url) {
        return (
          <div className="flex items-center gap-2 text-gray-500 bg-gray-100 dark:bg-gray-600 rounded-full px-4 py-2">
            <Play className="w-5 h-5" />
            <span>🎵 Nota de voz</span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-3 bg-gray-100 dark:bg-gray-600 rounded-full px-4 py-2">
          <Play className="w-6 h-6 text-green-500 flex-shrink-0" />
          <audio controls className="h-10" style={{ width: '220px' }}>
            <source src={media_url} type={mimetype || 'audio/webm'} />
            <source src={media_url} type="audio/ogg" />
            <source src={media_url} type="audio/mpeg" />
            Tu navegador no soporta audio.
          </audio>
        </div>
      );
    }
    if (message_type === 'sticker' && media_url) {
      return <img src={media_url} alt="Sticker" className="w-32 h-32 object-contain" loading="lazy" />;
    }
    if (message_type === 'video') {
      return (
        <div className="flex items-center gap-2 text-gray-500">
          <Play className="w-5 h-5" />
          <span>🎥 Video{content ? ': ' + content : ''}</span>
        </div>
      );
    }
    if (message_type === 'document') {
      const isPdf = filename?.toLowerCase().endsWith('.pdf') || mimetype?.includes('pdf');
      return (
        <div className="space-y-2">
          {isPdf && media_url && (
            <div className="relative bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden" style={{ height: '200px' }}>
              <iframe src={media_url + '#toolbar=0&navpanes=0'} className="w-full h-full" title="PDF Preview" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                <p className="text-white text-xs truncate">{filename}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 bg-gray-100 dark:bg-gray-600 rounded-lg px-4 py-3">
            <FileText className={"w-10 h-10 " + (isPdf ? "text-red-500" : "text-blue-500")} />
            <div className="flex-1">
              <p className="font-medium text-sm">{filename || content || 'Documento'}</p>
              <p className="text-xs text-gray-400">{isPdf ? 'PDF' : 'Documento'}</p>
            </div>
            {media_url && (
              <>
                <a href={media_url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full" title="Abrir"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></a>
                <a href={media_url} download className="p-2 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full" title="Descargar"><Download className="w-5 h-5" /></a>
              </>
            )}
          </div>
        </div>
      );
    }
    return <p className="text-gray-900 dark:text-white whitespace-pre-wrap break-words">{renderTextWithMentions(content)}</p>;
  };

  const renderMessageReactions = (msg) => {
    const reactions = Array.isArray(msg.reactions) ? msg.reactions.filter(r => r?.emoji) : [];
    if (!reactions.length) return null;
    const grouped = reactions.reduce((acc, r) => {
      const emoji = r.emoji || '✅';
      acc[emoji] = (acc[emoji] || 0) + 1;
      return acc;
    }, {});
    const label = reactions.map(r => `${r.emoji} ${r.actor_name || r.actor || 'WhatsApp'}`).join(', ');
    return (
      <div
        data-testid={`wa-message-reactions-${msg.id}`}
        className={`mt-1 flex ${msg.is_from_me ? 'justify-end' : 'justify-start'}`}
        title={label}
        aria-label={label}
      >
        <div className="inline-flex min-h-[20px] items-center gap-1 rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-xs leading-none shadow-sm dark:border-gray-600 dark:bg-gray-800">
          {Object.entries(grouped).map(([emoji, count]) => (
            <span key={emoji} className="flex items-center leading-none">
              <span>{emoji}</span>
              {count > 1 && <span className="ml-0.5 text-[10px] text-gray-500">{count}</span>}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const getConversationSubtitle = (conv) => {
    if (!conv) return '';
    if (isPrivateChat(conv.group_id)) return '+' + conv.group_id.replace('@s.whatsapp.net', '');
    if (conv.participant_subtitle) return conv.participant_subtitle;
    if (Array.isArray(conv.participant_preview) && conv.participant_preview.length) return conv.participant_preview.join(', ');
    if (conv.participant_count) return `${conv.participant_count} participantes`;
    return '';
  };
  const ConversationAvatar = ({ conv, size = 'md' }) => {
    const cls = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';
    const icon = size === 'lg' ? 'w-5 h-5' : 'w-5 h-5';
    if (conv?.profile_picture_url) {
      return (
        <img
          data-testid={`wa-conversation-avatar-${conv.group_id}`}
          src={conv.profile_picture_url}
          alt={conv.group_name || 'WhatsApp'}
          className={`${cls} rounded-full object-cover bg-gray-200 dark:bg-gray-700`}
          referrerPolicy="no-referrer"
        />
      );
    }
    return (
      <div data-testid={`wa-conversation-avatar-${conv?.group_id || 'empty'}`} className={`${cls} rounded-full flex items-center justify-center text-white font-semibold ` + (isPrivateChat(conv?.group_id) ? "bg-gradient-to-br from-blue-400 to-blue-600" : "bg-gradient-to-br from-green-400 to-green-600")}>
        {isPrivateChat(conv?.group_id) ? <MessageCircle className={icon} /> : <Users className={icon} />}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
          <p className="text-gray-500">Cargando conversaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="wa-conversations-page">
      {imagePreview && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setImagePreview(null)}
          data-testid="wa-image-preview-modal"
        >
          <div className="absolute left-4 top-4 max-w-[70vw] text-white" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold truncate">{imagePreview.msg?.from_name || imagePreview.msg?.from || 'WhatsApp'}</p>
            <p className="text-xs text-white/70">{formatDateSeparator(imagePreview.msg?.timestamp)} · {formatMessageTime(imagePreview.msg?.timestamp)}</p>
          </div>
          <button
            data-testid="wa-image-preview-close-button"
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full"
            onClick={(e) => { e.stopPropagation(); setImagePreview(null); }}
            aria-label="Cerrar preview"
          >
            <X className="w-8 h-8" />
          </button>
          <img src={imagePreview.src} alt="Preview" className="max-w-[92vw] max-h-[82vh] object-contain" onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <a
              data-testid="wa-image-preview-download-link"
              href={imagePreview.src}
              download
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow hover:bg-gray-100"
            >
              <Download className="w-4 h-4" /> Descargar
            </a>
          </div>
        </div>
      )}

      {/* Modal Nueva Conversación */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNewChat(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-500" /> Nueva Conversación
              </h3>
              <button onClick={() => setShowNewChat(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número de teléfono *</label>
                <input
                  type="tel"
                  value={newChatPhone}
                  onChange={(e) => setNewChatPhone(e.target.value)}
                  placeholder="5493511234567"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-400 mt-1">Incluir código de país sin + ni espacios</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre (opcional)</label>
                <input
                  type="text"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                  placeholder="Juan Pérez"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 focus:ring-2 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mensaje *</label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 focus:ring-2 focus:ring-green-500 resize-none"
                />
              </div>
              
              <button
                onClick={handleSendNewChat}
                disabled={!newChatPhone.trim() || !newMessage.trim() || sendingNew || botStatus.status !== 'connected'}
                className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sendingNew ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" /> Enviar mensaje
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Participantes */}
      {showParticipants && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowParticipants(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" /> 
                Participantes {participants && `(${participants.participant_count})`}
              </h3>
              <button onClick={() => setShowParticipants(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingParticipants ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
              </div>
            ) : participants ? (
              <div className="flex-1 overflow-y-auto space-y-2">
                {participants.participants.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className={"w-10 h-10 rounded-full flex items-center justify-center text-white " + (p.isSuperAdmin ? "bg-yellow-500" : p.isAdmin ? "bg-blue-500" : "bg-gray-400")}>
                      {p.isSuperAdmin || p.isAdmin ? <Shield className="w-5 h-5" /> : <UserCircle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">+{p.phone}</p>
                      <p className="text-xs text-gray-500">
                        {p.isSuperAdmin ? 'Super Admin' : p.isAdmin ? 'Admin' : 'Miembro'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-green-500" />WhatsApp en Vivo
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Mensajes en tiempo real</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={"flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium " + (botStatus.status === 'connected' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400')}>
            <Bot className="w-3.5 h-3.5" /><span className="hidden sm:inline">{botStatus.status === 'connected' ? 'Bot Online' : 'Bot Offline'}</span>
          </div>
          <div className={"flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium " + (isConnected ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400')}>
            {isConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5 animate-pulse" />}
            <span className="hidden sm:inline">{isConnected ? 'Live' : 'Offline'}</span>
          </div>
          <button data-testid="wa-refresh-conversations-button" onClick={() => { loadConversations(); checkBotStatus(); }} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700" title="Actualizar" aria-label="Actualizar conversaciones">
            <RefreshCw className="w-5 h-5 text-gray-500" />
          </button>
          <button data-testid="wa-import-groups-button" onClick={handleImportGroups} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700" title="Importar todos los grupos" aria-label="Importar grupos">
            <FolderDown className="w-5 h-5 text-blue-500" />
          </button>
          <button data-testid="wa-new-chat-button" onClick={() => setShowNewChat(true)} className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all" title="Nueva conversación" aria-label="Nueva conversación">
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className={"w-full md:w-96 border-r border-gray-200 dark:border-gray-700 flex flex-col " + (showMobileChat ? 'hidden md:flex' : 'flex')}>
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input data-testid="wa-conversation-search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Buscar conversación..." className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500">Sin conversaciones</p>
              </div>
            ) : (
              filteredConversations.map(conv => (
                <div key={conv.group_id} data-testid={`wa-conversation-item-${conv.group_id}`} onClick={() => setSelectedConversation(conv)} className={"flex items-center gap-3 p-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 transition-all " + (selectedConversation?.group_id === conv.group_id ? 'bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50')}>
                  <ConversationAvatar conv={conv} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">{conv.group_name || 'Contacto'}</h3>
                      <span className="text-xs text-gray-400">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-sm text-gray-500 truncate pr-2">
                        {conv.last_message_from && <span className="font-medium">{conv.last_message_from}: </span>}
                        {conv.last_message || getConversationSubtitle(conv) || 'Sin mensajes'}
                      </p>
                      {conv.unread_count > 0 && <span className="min-w-[20px] h-5 px-1.5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">{conv.unread_count}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={"flex-1 flex flex-col " + (!showMobileChat && !selectedConversation ? 'hidden md:flex' : 'flex')}>
          {!selectedConversation ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-gray-50 dark:bg-gray-900/50">
              <div className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <MessageCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">WhatsApp en Vivo</h2>
              <p className="text-gray-500">Selecciona una conversación</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <button data-testid="wa-back-to-conversations-button" onClick={() => { setShowMobileChat(false); setSelectedConversation(null); }} className="md:hidden p-1" aria-label="Volver a conversaciones"><ChevronLeft className="w-6 h-6" /></button>
                <ConversationAvatar conv={selectedConversation} />
                <div className="flex-1">
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" placeholder="Nombre del contacto" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} />
                      <button onClick={handleSaveName} className="p-1 text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"><Check className="w-5 h-5" /></button>
                      <button onClick={() => setEditingName(false)} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{selectedConversation.group_name || 'Contacto'}</h3>
                      {isPrivateChat(selectedConversation.group_id) && (
                        <button data-testid="wa-edit-contact-name-button" onClick={() => { setEditingName(true); setNewName(selectedConversation.group_name || ''); }} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Editar nombre" aria-label="Editar nombre">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {!isPrivateChat(selectedConversation.group_id) && (
                        <button data-testid="wa-show-participants-button" onClick={handleShowParticipants} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Ver participantes" aria-label="Ver participantes">
                          <Users className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 truncate" data-testid="wa-conversation-subtitle">{getConversationSubtitle(selectedConversation) || selectedConversation.group_id}</p>
                </div>
              </div>

              <div
                ref={messagesScrollRef}
                onScroll={handleMessagesScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#e5ddd5] dark:bg-gray-900"
                data-testid="wa-messages-scroll-container"
              >
                {loadingMessages ? (
                  <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div></div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full"><p className="text-gray-500 bg-white/80 dark:bg-gray-800/80 px-4 py-2 rounded-lg">No hay mensajes</p></div>
                ) : (
                  <>
                    {hasMoreMessages && (
                      <div className="flex justify-center py-2">
                        <button
                          data-testid="wa-load-older-messages-button"
                          onClick={loadOlderMessages}
                          disabled={loadingOlderMessages}
                          className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-medium text-gray-600 shadow-sm transition hover:bg-white disabled:opacity-60 dark:bg-gray-800 dark:text-gray-300"
                        >
                          {loadingOlderMessages ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                          {loadingOlderMessages ? 'Cargando historial...' : 'Cargar mensajes anteriores'}
                        </button>
                      </div>
                    )}
                    {messages.map((msg, idx) => {
                      const showDate = idx === 0 || !sameMessageDay(msg.timestamp, messages[idx - 1]?.timestamp);
                      return (
                        <React.Fragment key={msg.id || idx}>
                          {showDate && (
                            <div className="flex justify-center" data-testid={`wa-date-separator-${msg.id || idx}`}>
                              <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium capitalize text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                                {formatDateSeparator(msg.timestamp)}
                              </span>
                            </div>
                          )}
                          <div className={"flex " + (msg.is_from_me ? 'justify-end' : 'justify-start') + " group"} data-testid={`wa-message-row-${msg.id || idx}`}>
                            <div className={"max-w-[75%] rounded-lg px-3 py-2 shadow-sm relative " + (msg.is_from_me ? 'bg-[#dcf8c6] dark:bg-green-800 rounded-tr-none' : 'bg-white dark:bg-gray-700 rounded-tl-none')}>
                              <button 
                                data-testid={`wa-message-reply-button-${msg.id || idx}`}
                                onClick={() => setReplyingTo(msg)}
                                className={"absolute top-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 " + (msg.is_from_me ? 'left-[-30px]' : 'right-[-30px]')}
                                title="Responder"
                                aria-label="Responder mensaje"
                              >
                                <Reply className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                              </button>
                              {!msg.is_from_me && (
                                <p
                                  className="text-xs font-semibold mb-1 tracking-wide"
                                  style={{ color: getParticipantColor(msg) }}
                                  data-testid={`wa-participant-name-${msg.id || idx}`}
                                >
                                  {msg.from_name || msg.from}
                                </p>
                              )}
                              {msg.quoted_content && (
                                <div className="mb-2 pl-2 border-l-2 border-green-500 bg-black/5 dark:bg-white/5 rounded py-1 px-2">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{msg.quoted_content}</p>
                                </div>
                              )}
                              {renderMessageContent(msg)}
                              <div className={"flex items-center justify-end gap-1 mt-1 " + (msg.is_from_me ? 'text-green-700 dark:text-green-300' : 'text-gray-400')}>
                                <span className="text-[10px]">{formatMessageTime(msg.timestamp)}</span>
                              </div>
                              {renderMessageReactions(msg)}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                {/* Barra de vista previa de respuesta */}
                {replyingTo && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-white dark:bg-gray-700 rounded-lg border-l-4 border-green-500">
                    <CornerUpLeft className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-green-600 dark:text-green-400">{replyingTo.from_name || replyingTo.from}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{replyingTo.content || (replyingTo.message_type === 'image' ? '📷 Imagen' : replyingTo.message_type === 'audio' ? '🎵 Audio' : '[Media]')}</p>
                    </div>
                    <button onClick={() => setReplyingTo(null)} className="p-1 text-gray-400 hover:text-red-500 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Input oculto para archivos */}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileSelect} 
                  className="hidden" 
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
                
                {botStatus.status !== 'connected' ? (
                  <div className="flex items-center justify-center gap-2 py-3 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <Clock className="w-5 h-5" /><span className="text-sm">Bot offline - Esperando conexión</span>
                  </div>
                ) : selectedFile ? (
                  /* Vista previa de archivo seleccionado */
                  <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded-lg">
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm truncate">{selectedFile.name}</span>
                      <span className="text-xs text-gray-400">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <input 
                      type="text" 
                      value={newMessage} 
                      onChange={(e) => setNewMessage(e.target.value)} 
                      placeholder="Añadir descripción..." 
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-full text-sm"
                    />
                    <button onClick={() => setSelectedFile(null)} className="p-2 text-gray-400 hover:text-red-500">
                      <X className="w-5 h-5" />
                    </button>
                    <button onClick={handleSendFile} disabled={sending} className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all disabled:opacity-50">
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                ) : isRecording ? (
                  /* Grabando audio con visualizador */
                  <div className="flex items-center gap-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                    <canvas 
                      ref={canvasRef} 
                      width="200" 
                      height="40" 
                      className="flex-1 rounded bg-red-100 dark:bg-red-900/30"
                    />
                    <span className="text-sm font-mono text-red-600 dark:text-red-400 min-w-[45px]">
                      {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </span>
                    <button onClick={stopRecording} className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all shadow-lg" title="Detener grabación">
                      <Square className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  /* Input normal con botones de media */
                  <div className="flex items-center gap-2">
                    <button data-testid="wa-attach-file-button" onClick={() => fileInputRef.current?.click()} className="p-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all" title="Adjuntar archivo" aria-label="Adjuntar archivo">
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <div className="relative flex-1">
                      {mentionMenuOpen && (
                        <div
                          data-testid="wa-mention-suggestions-menu"
                          className="absolute bottom-full left-0 right-0 z-20 mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
                        >
                          {mentionSuggestions.length ? mentionSuggestions.map(target => (
                            <button
                              key={target.jid}
                              type="button"
                              data-testid={`wa-mention-suggestion-${target.jid}`}
                              onMouseDown={(e) => { e.preventDefault(); insertMention(target); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-green-50 dark:hover:bg-green-900/20"
                            >
                              <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: getParticipantColor(target.label) }}>
                                {target.label.slice(0, 1).toUpperCase()}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white">@{target.label}</span>
                                <span className="block truncate text-xs text-gray-500">{target.phone ? `+${target.phone}` : target.jid}</span>
                              </span>
                            </button>
                          )) : (
                            <div data-testid="wa-mention-no-results" className="px-3 py-2 text-sm text-gray-500">
                              Sin coincidencias para @{mentionQuery}
                            </div>
                          )}
                        </div>
                      )}
                      <input data-testid="wa-message-input" ref={inputRef} type="text" value={newMessage} onChange={handleMessageInputChange} onKeyDown={handleKeyPress} placeholder="Escribe un mensaje..." className="w-full px-4 py-3 bg-white dark:bg-gray-700 border-0 rounded-full text-sm focus:ring-2 focus:ring-green-500" disabled={sending} />
                    </div>
                    {newMessage.trim() ? (
                      <button data-testid="wa-send-message-button" onClick={handleSend} disabled={sending} className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all disabled:opacity-50" aria-label="Enviar mensaje">
                        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </button>
                    ) : (
                      <button data-testid="wa-record-audio-button" onClick={startRecording} className="p-3 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full transition-all" title="Grabar nota de voz" aria-label="Grabar nota de voz">
                        <Mic className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
